import type { ComputedRef } from "vue";
import type { useComponentContext } from "composables/useComponentContext";
import type { useStateStore } from "composables/useStateStore";
import type { TableProps } from "./TableProps";
import type { TableHeaderCellProps } from "./TableHeaderCellProps";
import type { ComponentState } from "types/componentState";

// ─── Component type constants ────────────────────────────────────────────────

export const TABLE_TYPE = "Table";
export const TABLE_HEAD_TYPE = "TableHead";
export const TABLE_BODY_TYPE = "TableBody";
export const TABLE_FOOTER_TYPE = "TableFooter";
export const TABLE_ROW_TYPE = "TableRow";
export const TABLE_CELL_TYPE = "TableCell";
export const TABLE_HEADER_CELL_TYPE = "TableHeaderCell";

// ─── Enums ───────────────────────────────────────────────────────────────────

/** Sort direction for table columns. */
export enum SortDirection {
  None = "none",
  Ascending = "asc",
  Descending = "desc",
}

// ─── Interfaces ──────────────────────────────────────────────────────────────

/** Column width information stored per column index. */
export interface ColumnWidth {
  index: number;
  width: number;
  minWidth?: number;
  maxWidth?: number;
}

/**
 * Shared context passed to all table sub-composables.
 *
 * Created once by `useTable()` and injected into each concern-specific
 * composable (`useTableSorting`, `useTableSelection`, etc.).
 *
 * **When adding a new sub-composable**, accept `TableContext` as the first
 * parameter rather than calling `useComponentContext()` / `useStateStore()`
 * directly — this ensures all composables share the same reactive references.
 *
 * @example
 * ```ts
 * export function useTableFiltering(ctx: TableContext) {
 *   const { tableProps, bodyUid, stateStore } = ctx;
 *   // ...
 * }
 * ```
 */
export interface TableContext {
  /** The component context (uid, typeName) of the calling component. */
  context: ReturnType<typeof useComponentContext>;

  /** The shared stateStore instance for reading/writing component state. */
  stateStore: ReturnType<typeof useStateStore<TableProps>>;

  /** UID of the parent `Table` component (or own UID if the component *is* the Table). */
  tableUid: ComputedRef<number | null>;

  /** Reactive props of the parent Table component. */
  tableProps: ComputedRef<TableProps | undefined>;

  /** UID of the first `<e-table-head>` child. */
  headUid: ComputedRef<number | null>;

  /** UID of the first `<e-table-body>` child. */
  bodyUid: ComputedRef<number | null>;

  /** All header cells from the first row in `<e-table-head>`. */
  headerCells: ComputedRef<ComponentState<TableHeaderCellProps>[]>;

  /** Row index of the current component within the table body (-1 if not in body). */
  currentRowIndex: ComputedRef<number>;

  /** Column index of the current component within its parent row. */
  currentColumnIndex: ComputedRef<number>;
}
